import React, { useEffect, useMemo, useState } from "react";
import { api, API_BASE, getToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PackageSearch, Plus, Trash2, X, MapPin, FileSpreadsheet, FileText, Maximize2, Minimize2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const HEADER_BLUE = "bg-[#2f5597]";

const downloadExport = async (fmt, mid = null, mids = null) => {
  try {
    let q = `fmt=${fmt}`;
    if (mid) q += `&mid=${mid}`;
    if (mids && mids.length) q += `&mids=${mids.join(",")}`;
    const resp = await fetch(`${API_BASE}/spares-monitoring-export?${q}`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!resp.ok) throw new Error();
    const cd = resp.headers.get("Content-Disposition") || "";
    const filename = (cd.match(/filename="([^"]+)"/) || [])[1] || `spares-report.${fmt}`;
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`${filename} downloaded`);
  } catch {
    toast.error("Export failed");
  }
};

const editablePayload = (d) => ({
  title: d.title || "",
  part_number: d.part_number,
  bases: (d.bases || []).map((b) => ({
    code: b.code,
    stk: Number(b.stk) || 0,
    min: b.min === null || b.min === "" ? null : Number(b.min),
    max: b.max === null || b.max === "" ? null : Number(b.max),
    notes: b.notes || null,
  })),
  flyaway_kit: Number(d.flyaway_kit) || 0,
  on_order: d.on_order,
  on_repair: d.on_repair,
  return_to_inventory: d.return_to_inventory,
});

function NumCell({ value, onCommit, testid, className = "", danger = false }) {
  const [v, setV] = useState(value ?? "");
  useEffect(() => { setV(value ?? ""); }, [value]);
  return (
    <input
      type="number"
      min="0"
      className={`w-14 h-7 px-1 text-center text-sm border rounded outline-none ${danger ? "border-red-400 bg-red-100 text-red-700 font-bold focus:border-red-500" : "border-summit-hairline bg-white focus:border-summit-primary"} ${className}`}
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => { if (String(v) !== String(value ?? "")) onCommit(v === "" ? null : Number(v)); }}
      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
      data-testid={testid}
    />
  );
}

function TextCommit({ value, onCommit, placeholder, testid, className = "", type = "text" }) {
  const [v, setV] = useState(value ?? "");
  useEffect(() => { setV(value ?? ""); }, [value]);
  return (
    <input
      type={type}
      className={`h-7 px-2 text-sm border border-summit-hairline rounded bg-white focus:border-summit-primary outline-none ${className}`}
      placeholder={placeholder}
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => { if ((v || "") !== (value || "")) onCommit(v); }}
      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
      data-testid={testid}
    />
  );
}

const PENDING_ROWS = [
  ["on_order", "on order"],
  ["on_repair", "on repair"],
  ["return_to_inventory", "return to inventory"],
];

function MonitorCard({ doc, systemBases, focused, onToggleFocus, onChanged, onDeleted }) {
  const [d, setD] = useState(doc);
  const [newBase, setNewBase] = useState("");
  useEffect(() => { setD(doc); }, [doc]);

  const patch = async (partial) => {
    const next = { ...d, ...partial };
    setD(next);
    try {
      const r = await api.patch(`/spares-monitoring/${d.id}`, editablePayload(next));
      setD(r.data);
      onChanged(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
      setD(doc);
    }
  };

  const setBase = (i, key, val) => {
    const bases = (d.bases || []).map((b, j) => (j === i ? { ...b, [key]: val } : b));
    patch({ bases });
  };

  const addBase = () => {
    const code = newBase.trim().toUpperCase();
    if (!code) return;
    if ((d.bases || []).some((b) => b.code === code)) { toast.error(`${code} is already listed`); return; }
    setNewBase("");
    patch({ bases: [...(d.bases || []), { code, stk: 0, min: null, max: null, notes: null }] });
  };

  const removeBase = (i) => {
    const b = d.bases[i];
    if (!window.confirm(`Remove base ${b.code} from ${d.part_number}?`)) return;
    patch({ bases: d.bases.filter((_, j) => j !== i) });
  };

  const setPending = (key, field, val) => {
    patch({ [key]: { ...(d[key] || {}), [field]: val } });
  };

  const remove = async () => {
    if (!window.confirm(`Stop monitoring ${d.part_number}?`)) return;
    try {
      await api.delete(`/spares-monitoring/${d.id}`);
      toast.success(`${d.part_number} removed`);
      onDeleted(d.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const stkTotal = (d.bases || []).reduce((s, b) => s + (Number(b.stk) || 0), 0);
  const total = stkTotal + (Number(d.flyaway_kit) || 0) + (Number(d.on_repair?.qty) || 0) + (Number(d.return_to_inventory?.qty) || 0);
  const req = (b) => (b.max === null || b.max === "" || b.max === undefined ? null : (Number(b.max) || 0) - (Number(b.stk) || 0));
  const remaining = systemBases.filter((c) => !(d.bases || []).some((b) => b.code === c));

  return (
    <div className="border border-summit-hairline rounded-lg bg-white overflow-hidden shadow-sm" data-testid={`sm-card-${d.id}`}>
      <div className={`${HEADER_BLUE} text-white flex items-center px-3 py-2`}>
        <TextCommit
          value={d.title}
          onCommit={(v) => patch({ title: v })}
          placeholder="Report title — e.g. RJ NOSEWHEELS - Weekly Report"
          testid={`sm-title-${d.id}`}
          className="flex-1 !bg-transparent !border-transparent hover:!border-white/40 text-center font-display font-semibold !text-white placeholder:text-white/50"
        />
        <button className="p-1 rounded text-white/70 hover:text-white hover:bg-white/15 shrink-0" onClick={onToggleFocus} title={focused ? "Show all cards" : "Focus on this card"} data-testid={`sm-focus-${d.id}`}>
          {focused ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </button>
        <button className="p-1 rounded text-white/70 hover:text-white hover:bg-white/15 shrink-0" onClick={() => downloadExport("xlsx", d.id)} title="Download Excel weekly report" data-testid={`sm-export-xlsx-${d.id}`}>
          <FileSpreadsheet className="w-4 h-4" />
        </button>
        <button className="p-1 rounded text-white/70 hover:text-white hover:bg-white/15 shrink-0" onClick={() => downloadExport("pdf", d.id)} title="Download PDF weekly report" data-testid={`sm-export-pdf-${d.id}`}>
          <FileText className="w-4 h-4" />
        </button>
        <button className="p-1 rounded text-white/70 hover:text-white hover:bg-white/15 shrink-0" onClick={remove} title="Stop monitoring this part" data-testid={`sm-delete-${d.id}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div className="text-center py-1.5 font-display text-sm font-semibold" data-testid={`sm-pn-${d.id}`}>
        Part number {d.part_number}
      </div>
      <div className="flex items-center justify-between px-6 pb-1">
        <span className="font-display text-sm font-bold">Total Spares Owned</span>
        <span className="font-display text-lg font-bold text-[#2f5597]" data-testid={`sm-total-${d.id}`}>{total}</span>
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr className="border-y border-gray-800 font-display font-bold text-[13px]">
            <th className="py-1 px-2 text-center w-[14%]">Status</th>
            <th className="py-1 px-1 text-center w-[9%]">Stk</th>
            <th className="py-1 px-1 text-center w-[9%]">Min</th>
            <th className="py-1 px-1 text-center w-[9%]">Max</th>
            <th className="py-1 px-1 text-center w-[8%]">Req</th>
            <th className="py-1 px-2 text-center">Notes</th>
          </tr>
        </thead>
        <tbody>
          {(d.bases || []).map((b, i) => {
            const r = req(b);
            return (
              <tr key={b.code} className="border-b border-summit-hairline" data-testid={`sm-base-row-${d.id}-${b.code}`}>
                <td className="py-1 px-2 text-center">
                  <span className="inline-flex items-center gap-1 font-semibold">
                    {b.code}
                    <button className="p-0.5 rounded text-gray-300 hover:text-rose-600" onClick={() => removeBase(i)} title={`Remove ${b.code}`} data-testid={`sm-base-remove-${d.id}-${b.code}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                </td>
                <td className="py-1 px-1 text-center"><NumCell value={b.stk ?? 0} danger={(Number(b.stk) || 0) === 0} onCommit={(v) => setBase(i, "stk", v ?? 0)} testid={`sm-stk-${d.id}-${b.code}`} /></td>
                <td className="py-1 px-1 text-center"><NumCell value={b.min} onCommit={(v) => setBase(i, "min", v)} testid={`sm-min-${d.id}-${b.code}`} /></td>
                <td className="py-1 px-1 text-center"><NumCell value={b.max} onCommit={(v) => setBase(i, "max", v)} testid={`sm-max-${d.id}-${b.code}`} /></td>
                <td className={`py-1 px-1 text-center font-bold ${r === null ? "" : r > 0 ? "text-red-600" : "text-emerald-600"}`} data-testid={`sm-req-${d.id}-${b.code}`}>
                  {r === null ? "" : r}
                </td>
                <td className="py-1 px-2"><TextCommit value={b.notes} onCommit={(v) => setBase(i, "notes", v)} testid={`sm-notes-${d.id}-${b.code}`} className="w-full" /></td>
              </tr>
            );
          })}
          <tr className="border-b border-summit-hairline">
            <td className="py-1 px-2 text-right font-mono text-[10px] uppercase tracking-widest text-gray-400">Subtotal</td>
            <td className="py-1 px-1 text-center font-bold" data-testid={`sm-subtotal-${d.id}`}>{stkTotal}</td>
            <td colSpan={4} className="py-1 px-2">
              <div className="flex items-center gap-1.5 justify-end">
                <MapPin className="w-3 h-3 text-gray-400" />
                <input
                  list={`sm-bases-${d.id}`}
                  className="h-7 w-24 px-2 text-xs border border-summit-hairline rounded bg-white uppercase outline-none focus:border-summit-primary"
                  placeholder="Add base"
                  value={newBase}
                  onChange={(e) => setNewBase(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") addBase(); }}
                  data-testid={`sm-add-base-input-${d.id}`}
                />
                <datalist id={`sm-bases-${d.id}`}>
                  {remaining.map((c) => <option key={c} value={c} />)}
                </datalist>
                <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={addBase} data-testid={`sm-add-base-btn-${d.id}`}>
                  <Plus className="w-3 h-3 mr-0.5" /> Base
                </Button>
              </div>
            </td>
          </tr>
          <tr className="border-b border-summit-hairline">
            <td className="py-1 px-2 text-center font-display font-bold text-[13px]">Flyaway kit</td>
            <td className="py-1 px-1 text-center"><NumCell value={d.flyaway_kit ?? 0} onCommit={(v) => patch({ flyaway_kit: v ?? 0 })} testid={`sm-flyaway-${d.id}`} /></td>
            <td colSpan={4} />
          </tr>
        </tbody>
      </table>

      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-800 font-display font-bold text-[13px]">
            <th className="py-1 px-2 text-center w-[23%]">Pending</th>
            <th className="py-1 px-1 text-center w-[9%]">Qty</th>
            <th className="py-1 px-1 text-center w-[18%]">ETA</th>
            <th className="py-1 px-2 text-center">Comments</th>
          </tr>
        </thead>
        <tbody>
          {PENDING_ROWS.map(([key, label]) => (
            <tr key={key} className="border-b border-summit-hairline" data-testid={`sm-pending-${d.id}-${key}`}>
              <td className="py-1 px-2 text-center">{label}</td>
              <td className="py-1 px-1 text-center"><NumCell value={d[key]?.qty ?? 0} onCommit={(v) => setPending(key, "qty", v ?? 0)} testid={`sm-${key}-qty-${d.id}`} /></td>
              <td className="py-1 px-1 text-center"><TextCommit type="date" value={d[key]?.eta} onCommit={(v) => setPending(key, "eta", v)} testid={`sm-${key}-eta-${d.id}`} className="w-full" /></td>
              <td className="py-1 px-2"><TextCommit value={d[key]?.comments} onCommit={(v) => setPending(key, "comments", v)} testid={`sm-${key}-comments-${d.id}`} className="w-full" /></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="px-3 py-1.5 font-mono text-[9px] uppercase tracking-widest text-gray-400 text-right">
        Updated {d.updated_by_name} · {(d.updated_at || "").slice(0, 10)}
      </div>
    </div>
  );
}

export default function SparesMonitoring() {
  const { user } = useAuth();
  const [docs, setDocs] = useState(null);
  const [systemBases, setSystemBases] = useState([]);
  const [title, setTitle] = useState("");
  const [pn, setPn] = useState("");
  const [busy, setBusy] = useState(false);
  const [focusId, setFocusId] = useState(null);
  const [exportSel, setExportSel] = useState(null); // { fmt, ids: [] }
  const [exporting, setExporting] = useState(false);

  const hasAccess = user && (user.spares_monitoring_access || user.role === "admin");

  useEffect(() => {
    if (!hasAccess) return;
    api.get("/spares-monitoring").then((r) => setDocs(r.data || [])).catch(() => setDocs([]));
    api.get("/spares-monitoring/bases").then((r) => setSystemBases(r.data || [])).catch(() => {});
  }, [hasAccess]);

  const add = async () => {
    if (!pn.trim()) { toast.error("Part number is required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/spares-monitoring", { title, part_number: pn });
      setDocs((ds) => [...(ds || []), r.data]);
      setTitle("");
      setPn("");
      toast.success(`${r.data.part_number} is now monitored`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add part");
    } finally {
      setBusy(false);
    }
  };

  const filtered = useMemo(() => docs || [], [docs]);
  const focusDoc = useMemo(() => (docs || []).find((d) => d.id === focusId) || null, [docs, focusId]);

  if (!user) return null;
  if (!hasAccess) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="sm-no-access">
        <PackageSearch className="w-8 h-8 mx-auto text-gray-300" />
        <h1 className="font-display text-2xl font-semibold mt-4">Spares Monitoring</h1>
        <p className="text-sm text-gray-500 mt-2">You don&apos;t have access to this page. Ask an admin to grant it in Admin Settings.</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Stores · MST</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight mt-1" data-testid="sm-heading">Spares Monitoring</h1>
          <p className="text-sm text-gray-500 mt-1">Track stock of key part numbers across every base — click any cell to edit, changes save automatically.</p>
        </div>
        {(docs || []).length > 0 && (
          <div className="flex items-center gap-2" data-testid="sm-export-all">
            <Button variant="outline" className="h-9" onClick={() => setExportSel({ fmt: "xlsx", ids: (docs || []).map((d) => d.id) })} data-testid="sm-export-all-xlsx">
              <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Summary .xlsx
            </Button>
            <Button variant="outline" className="h-9" onClick={() => setExportSel({ fmt: "pdf", ids: (docs || []).map((d) => d.id) })} data-testid="sm-export-all-pdf">
              <FileText className="w-4 h-4 mr-1.5" /> Summary .pdf
            </Button>
          </div>
        )}
      </div>

      <div className="mt-5 flex items-end gap-2 flex-wrap">
        <div>
          <span className="font-mono text-[9px] uppercase tracking-widest text-gray-500">Report title</span>
          <Input className="mt-0.5 h-9 w-56 text-sm" placeholder="e.g. RJ NOSEWHEELS - Weekly Report" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="sm-new-title" />
        </div>
        <div>
          <span className="font-mono text-[9px] uppercase tracking-widest text-gray-500">Part number *</span>
          <Input className="mt-0.5 h-9 w-36 text-sm uppercase" placeholder="e.g. AHA1349" value={pn} onChange={(e) => setPn(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") add(); }} data-testid="sm-new-pn" />
        </div>
        <Button className="h-9 bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={add} disabled={busy} data-testid="sm-add-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Monitor part
        </Button>
      </div>

      <Dialog open={!!exportSel} onOpenChange={(o) => { if (!o) setExportSel(null); }}>
        <DialogContent className="max-w-sm max-h-[85vh] overflow-y-auto" data-testid="sm-export-dialog">
          <DialogTitle className="font-display flex items-center gap-2">
            {exportSel?.fmt === "pdf" ? <FileText className="w-4 h-4" /> : <FileSpreadsheet className="w-4 h-4" />}
            Summary .{exportSel?.fmt} — select parts
          </DialogTitle>
          <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-widest">
            <button className="text-summit-primary hover:underline" onClick={() => setExportSel((s) => ({ ...s, ids: (docs || []).map((d) => d.id) }))} data-testid="sm-export-select-all">Select all</button>
            <button className="text-gray-400 hover:underline" onClick={() => setExportSel((s) => ({ ...s, ids: [] }))} data-testid="sm-export-select-none">None</button>
            <span className="ml-auto text-gray-400">{exportSel?.ids.length}/{(docs || []).length}</span>
          </div>
          <div className="space-y-1.5">
            {(docs || []).map((d) => {
              const on = !!exportSel?.ids.includes(d.id);
              return (
                <label key={d.id} className={`flex items-center gap-2.5 border rounded-md px-2.5 py-2 cursor-pointer transition-colors ${on ? "border-summit-primary bg-summit-glacier/30" : "border-summit-hairline"}`} data-testid={`sm-export-opt-${d.id}`}>
                  <input
                    type="checkbox"
                    className="accent-[#2f5597] w-4 h-4"
                    checked={on}
                    onChange={(e) => setExportSel((s) => ({ ...s, ids: e.target.checked ? [...s.ids, d.id] : s.ids.filter((x) => x !== d.id) }))}
                    data-testid={`sm-export-check-${d.id}`}
                  />
                  <span className="font-mono text-xs font-bold">{d.part_number}</span>
                  <span className="text-xs text-gray-500 truncate">{d.title}</span>
                </label>
              );
            })}
          </div>
          <Button
            className="w-full bg-summit-primary hover:bg-summit-primary-soft text-white"
            disabled={exporting || !exportSel?.ids.length}
            onClick={async () => {
              setExporting(true);
              const allSelected = exportSel.ids.length === (docs || []).length;
              await downloadExport(exportSel.fmt, null, allSelected ? null : exportSel.ids);
              setExporting(false);
              setExportSel(null);
            }}
            data-testid="sm-export-confirm"
          >
            {exporting ? "Exporting…" : `Export ${exportSel?.ids.length || 0} part${exportSel?.ids.length === 1 ? "" : "s"} (.${exportSel?.fmt})`}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!focusDoc} onOpenChange={(o) => { if (!o) setFocusId(null); }}>
        <DialogContent className="max-w-3xl p-0 gap-0 max-h-[90vh] overflow-y-auto border-0 bg-transparent shadow-none" data-testid="sm-focus-dialog">
          <DialogTitle className="sr-only">Focused part</DialogTitle>
          {focusDoc && (
            <MonitorCard
              doc={focusDoc}
              systemBases={systemBases}
              focused
              onToggleFocus={() => setFocusId(null)}
              onChanged={(nd) => setDocs((ds) => (ds || []).map((x) => (x.id === nd.id ? nd : x)))}
              onDeleted={(id) => { setDocs((ds) => (ds || []).filter((x) => x.id !== id)); setFocusId(null); }}
            />
          )}
        </DialogContent>
      </Dialog>

      {docs === null ? (
        <p className="text-sm text-gray-400 mt-10 text-center">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="mt-10 text-center text-gray-400" data-testid="sm-empty">
          <PackageSearch className="w-8 h-8 mx-auto text-gray-300" />
          <p className="text-sm mt-3">No parts monitored yet — add a part number above to start.</p>
        </div>
      ) : (
        <div className="mt-5 grid gap-6 xl:grid-cols-2" data-testid="sm-grid">
          {filtered.map((d) => (
            <MonitorCard
              key={d.id}
              doc={d}
              systemBases={systemBases}
              focused={false}
              onToggleFocus={() => setFocusId(d.id)}
              onChanged={(nd) => setDocs((ds) => (ds || []).map((x) => (x.id === nd.id ? nd : x)))}
              onDeleted={(id) => { setDocs((ds) => (ds || []).filter((x) => x.id !== id)); setFocusId((f) => (f === id ? null : f)); }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
